import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import headerConfig from '../config/headerConfig';

function Header({ onCVClick, cvVisible, setCVVisible, onVisibilityChange, headerVisible }) {
  const { name, people, address, contacts, shortName, cvLabel } = headerConfig;
  // Use external headerVisible prop if provided, otherwise default to true
  const [internalVisible] = useState(true);
  const visible = headerVisible !== undefined ? headerVisible : internalVisible;
  const headerRef = useRef(null);
  const location = useLocation();

  const getContactLink = (contact) => {
    switch (contact.label.toLowerCase()) {
      case 'email':
        return `mailto:${contact.value}`;
      case 'web':
        return contact.value.startsWith('http') ? contact.value : `https://${contact.value}`;
      case 'instagram':
        return `https://instagram.com/${contact.value.replace('@', '')}`;
      default:
        return '#';
    }
  };

  useEffect(() => {
    // Close CV when route changes
    if (cvVisible) {
      setCVVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  useEffect(() => {
    // Close CV when header hides
    if (!visible && cvVisible) {
      setCVVisible(false);
    }
  }, [visible, cvVisible, setCVVisible]);

  useEffect(() => {
    // Notify parent component of visibility changes
    if (onVisibilityChange) {
      onVisibilityChange(visible);
    }
  }, [visible, onVisibilityChange]);

  useEffect(() => {
    const measure = () => {
      if (headerRef.current) {
        const h = headerRef.current.offsetHeight;
        try {
          document.documentElement.style.setProperty('--header-height', `${h}px`);
        } catch (e) {}
      }
    };
    measure();
    const onResize = () => measure();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      try { document.documentElement.style.removeProperty('--header-height'); } catch (e) {}
    };
  }, []);

  const containerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    transform: visible ? 'translateY(0)' : 'translateY(-110%)',
    transition: 'transform 220ms ease',
    background: '#ffffff',
    boxShadow: 'none',
    borderBottom: '1px solid rgba(0,0,0,0.06)',
    pointerEvents: 'auto',
    fontSize: 14,
  };

  return (
    <div ref={headerRef} style={containerStyle}>
        <div style={{display: 'grid', gridTemplateColumns: '1fr auto 1fr', gridTemplateRows: 'repeat(3, auto)', alignItems: 'start', justifyItems: 'start', fontFamily: "Inter, sans-serif", width: '50%'}}>
          <div style={{display: 'grid', gridTemplateColumns: 'auto 0.2fr 1fr', gridColumn: '1', gridRow: '1', alignItems: 'start', justifyItems: 'start', fontFamily: "Inter, sans-serif"}}>
            <p style={{ gridColumn: '1', margin: 8 }}>
              {name}
            </p>
            <span style={{ gridColumn: '3', margin: 8, textAlign: 'justify', width: '320px' }}>
              {contacts.map((contact, idx) => (
                <span key={idx} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{contact.label}</span>
                  <a 
                    href={getContactLink(contact)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="header-link"
                    style={{ cursor: 'pointer' }}
                  >
                    {contact.value}
                  </a>
                </span>
              ))}
            </span>
          </div>

          <div style={{display: 'inline', gridColumn: '1', gridRow: '2', alignItems: 'start', justifyItems: 'start', fontFamily: "Inter, sans-serif"}}>
            <p style={{ gridColumn: '1', margin: 8, textAlign: 'start' }}>
              {people.map((person, idx) => (<span key={idx}>{person}{idx < people.length - 1 ? ' + ' : ''}<br /></span>))}
            </p>
          </div>

          <div style={{display: 'block', gridColumn: '1', gridRow: '3', alignItems: 'end', justifyItems: 'start', fontFamily: "Inter, sans-serif",}}>
            <p style={{ gridColumn: '1', margin: 8, textAlign: 'start' }}>
              {address.street}<br />
              <span style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{address.city}</span>
                <span>{address.country}</span>
              </span>
            </p>
          </div>

          <p 
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') window.location.href = '/'; }}
            style={{ gridColumn: '2', gridRow: '1', margin: 8, width: 'auto', justifySelf: 'center', textAlign: 'center', cursor: 'pointer' }}
            onClick={() => window.location.href = '/'}
          >
            <span className="hover-text-only">{shortName}</span>
          </p>

          <p
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onCVClick && onCVClick(); }}
            style={{ gridColumn: '2', gridRow: '3', margin: 8, width: 'auto', alignSelf: 'end', textAlign: 'center', cursor: 'pointer' }}
            onClick={onCVClick}
          >
            <span className="hover-text-only">{cvLabel}</span>
          </p>
          
        </div>
        {cvVisible && (
          <div style={{ width: '100%', height: '1px', backgroundColor: '#333333', margin: '0' }} />
        )}
      </div>
  );
}

export default Header;
